/**
 * FP Badge Animator — animates the navbar FP badge when balance changes.
 *
 * Subscribes to BalanceService and auto-triggers tier-aware animations.
 * Import and call init() from auth-status.ts after badge DOM is ready.
 *
 * Animation tiers (reads localStorage 'animation_pref'):
 *   - none:    Instant value snap, no visual effect
 *   - reduced: Gentle scale pulse (400ms) + gold border flash
 *   - playful: Bouncy scale (800ms) + gold glow + count-up tween
 *
 * For losses (negative delta): red flash + shake instead of gold glow.
 */

import { balanceService } from './balance-service';
import { logger } from '../lib/logger';
import { getAnimationPref, type AnimationTier } from '../lib/animation-pref';

/** CSS class prefixes applied to #fpBalanceDisplay */
const GAIN_CLASSES: Record<Exclude<AnimationTier, 'none'>, string> = {
  reduced: 'fp-badge-gain-reduced',
  playful: 'fp-badge-gain-playful',
};
const LOSS_CLASSES: Record<Exclude<AnimationTier, 'none'>, string> = {
  reduced: 'fp-badge-loss-reduced',
  playful: 'fp-badge-loss-playful',
};

/** All animation classes for bulk removal */
const ALL_ANIM_CLASSES = [
  ...Object.values(GAIN_CLASSES),
  ...Object.values(LOSS_CLASSES),
];

class FPBadgeAnimator {
  private previousBalance: number | null = null;
  private unsubscribe: (() => void) | null = null;
  private badgeEl: HTMLElement | null = null;
  private valueEl: HTMLElement | null = null;
  private tweenRAF: number | null = null;

  /**
   * Initialize the animator. Call once after #fpBalanceDisplay exists in DOM.
   * @param initialBalance - Seeds previousBalance so first callback is a no-op
   */
  init(initialBalance?: number): void {
    this.badgeEl = document.getElementById('fpBalanceDisplay');
    this.valueEl = document.getElementById('fpBalanceValue');

    if (typeof initialBalance === 'number') {
      this.previousBalance = initialBalance;
    }

    // Subscribe to balance changes
    if (!this.unsubscribe) {
      this.unsubscribe = balanceService.subscribe((newBalance: number) => {
        this.onBalanceChange(newBalance);
      });
    }
  }

  /** Teardown (for tests / SPA transitions) */
  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.cancelTween();
    this.badgeEl = null;
    this.valueEl = null;
    this.previousBalance = null;
  }

  /**
   * Force-trigger animation on specific elements. Used by admin demo page.
   * @param oldBalance - Starting value
   * @param newBalance - Ending value
   * @param badgeEl - Badge container element (defaults to navbar badge)
   * @param valueEl - Value text element (defaults to navbar value)
   */
  triggerAnimation(
    oldBalance: number,
    newBalance: number,
    badgeEl?: HTMLElement | null,
    valueEl?: HTMLElement | null,
  ): void {
    const badge = badgeEl ?? this.badgeEl;
    const value = valueEl ?? this.valueEl;
    const delta = newBalance - oldBalance;

    if (delta === 0 || !badge) return;

    if (delta > 0) {
      this.animateGain(badge, value, oldBalance, newBalance);
    } else {
      this.animateLoss(badge, value, oldBalance, newBalance);
    }
  }

  /** Read current animation tier from localStorage */
  getAnimationTier(): AnimationTier {
    return getAnimationPref();
  }

  private onBalanceChange(newBalance: number): void {
    if (this.previousBalance === null) {
      // First balance load — just set, no animation
      this.previousBalance = newBalance;
      return;
    }

    const delta = newBalance - this.previousBalance;
    this.previousBalance = newBalance;

    if (delta === 0) return;
    if (!this.badgeEl) return;

    if (delta > 0) {
      this.animateGain(this.badgeEl, this.valueEl, newBalance - delta, newBalance);
    } else {
      this.animateLoss(this.badgeEl, this.valueEl, newBalance - delta, newBalance);
    }
  }

  private animateGain(
    badge: HTMLElement,
    value: HTMLElement | null,
    oldBalance: number,
    newBalance: number,
  ): void {
    const tier = this.getAnimationTier();

    // Update value
    if (tier === 'playful' && value) {
      this.tweenValue(value, oldBalance, newBalance, 800);
    } else if (value) {
      value.textContent = newBalance.toLocaleString();
    }

    if (tier === 'none') return;

    const cls = GAIN_CLASSES[tier];
    this.applyAnimationClass(badge, cls);
  }

  private animateLoss(
    badge: HTMLElement,
    value: HTMLElement | null,
    oldBalance: number,
    newBalance: number,
  ): void {
    const tier = this.getAnimationTier();

    // Update value — always snap for losses (no tween)
    if (value) {
      value.textContent = newBalance.toLocaleString();
    }

    if (tier === 'none') return;

    const cls = LOSS_CLASSES[tier];
    this.applyAnimationClass(badge, cls);
  }

  private applyAnimationClass(el: HTMLElement, cls: string): void {
    // Cancel any in-flight animation
    ALL_ANIM_CLASSES.forEach(c => el.classList.remove(c));

    // Force reflow to restart animation
    void el.offsetWidth;

    el.classList.add(cls);

    // Self-remove after animation completes
    const handler = () => {
      el.classList.remove(cls);
      el.removeEventListener('animationend', handler);
    };
    el.addEventListener('animationend', handler);
  }

  private tweenValue(
    el: HTMLElement,
    from: number,
    to: number,
    durationMs: number,
  ): void {
    this.cancelTween();

    const start = performance.now();
    const delta = to - from;

    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);

      // Ease-out quad
      const eased = 1 - (1 - progress) * (1 - progress);
      const current = Math.round(from + delta * eased);
      el.textContent = current.toLocaleString();

      if (progress < 1) {
        this.tweenRAF = requestAnimationFrame(step);
      } else {
        this.tweenRAF = null;
      }
    };

    this.tweenRAF = requestAnimationFrame(step);
  }

  private cancelTween(): void {
    if (this.tweenRAF !== null) {
      cancelAnimationFrame(this.tweenRAF);
      this.tweenRAF = null;
    }
  }
}

/** Singleton instance */
export const fpBadgeAnimator = new FPBadgeAnimator();
